import React, { useEffect, useState } from 'react';
import { bedrockUsageService, OrgUsageRow } from '../../services/bedrockUsageService';
import '../../styles/BedrockUsage.css';

function relativeTime(isoString: string): string {
  const now = new Date();
  const date = new Date(isoString);
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays <= 30) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

interface Props {
  toolServerId: number;
  toolServerName: string;
  startDate: string;
  endDate: string;
  onSelectOrg: (id: number, name: string) => void;
}

const OrgUsageTable: React.FC<Props> = ({ toolServerId, toolServerName, startDate, endDate, onSelectOrg }) => {
  const [rows, setRows] = useState<OrgUsageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    bedrockUsageService
      .getOrgsByToolServer(toolServerId, startDate || undefined, endDate || undefined)
      .then((data) => {
        if (!cancelled) {
          setRows(data);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [toolServerId, startDate, endDate]);

  if (loading) {
    return (
      <div className="usage-table-container">
        <div className="usage-loading">Loading organisation usage…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="usage-table-container">
        <div className="usage-empty" style={{ color: 'var(--error-color, #f44336)' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="usage-table-container">
        <div className="usage-empty">No organisation usage found for this tool server in the selected period.</div>
      </div>
    );
  }

  return (
    <div className="usage-table-container">
      <table className="usage-table">
        <thead>
          <tr>
            <th>Organisation</th>
            <th>Total Calls</th>
            <th>Total Tokens</th>
            <th>Via This Server</th>
            <th>Via Other Servers</th>
            <th>Direct</th>
            <th>Active Users</th>
            <th>Last Active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.org_id}>
              <td>
                <span
                  className="usage-clickable-cell"
                  onClick={() => onSelectOrg(row.org_id, row.org_name)}
                >
                  {row.org_name}
                </span>
              </td>
              <td>{row.total_calls.toLocaleString()}</td>
              <td>{row.sum_actual_tokens.toLocaleString()}</td>
              <td>
                <span className="usage-source-badge primary">{row.via_this_server.toLocaleString()}</span>
              </td>
              <td>
                <span className="usage-source-badge secondary">{row.via_other_servers.toLocaleString()}</span>
              </td>
              <td>
                <span className="usage-source-badge direct">{row.direct_calls.toLocaleString()}</span>
              </td>
              <td>{row.active_users.toLocaleString()}</td>
              <td>{relativeTime(row.last_call)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="usage-table-note">
        Token counts reflect all activity attributed to this organisation, not just calls via {toolServerName}.
      </div>
    </div>
  );
};

export default OrgUsageTable;
